import authenticationConfig from "@/features/configuration";
import { SMART_DEBRIEF_API } from "@/config/const";
import axios, { AxiosError, AxiosResponse, type AxiosRequestConfig, type InternalAxiosRequestConfig } from "axios";

const axiosConfig: AxiosRequestConfig = {
  baseURL: SMART_DEBRIEF_API,
  headers: {
    "X-iBabs-Device-Id": authenticationConfig.authenticationDeviceKey,
  },
  withCredentials: true,
} as const;

export type AxiosConfig = AxiosRequestConfig;

class Axios {
  private axiosInstance;

  constructor() {
    this.axiosInstance = axios.create(axiosConfig);

    // default error interceptor: anything that bubbles up from a request
    // will be reported and (optionally) trigger a toast via the shared
    // reporting util.  Callers can still add their own interceptors later.
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        // lazy import to avoid circular dependency in some test setups
        console.log("error", error);
        return Promise.reject(error);
      },
    );
  }

  public get<T>(url: string, config?: AxiosConfig) {
    return this.axiosInstance.get<T>(url, config);
  }

  public post<T, D = unknown>(url: string, data?: D, config?: AxiosConfig) {
    return this.axiosInstance.post<T>(url, data, config);
  }

  public put<T, D = unknown>(url: string, data?: D, config?: AxiosConfig) {
    return this.axiosInstance.put<T>(url, data, config);
  }

  public delete<T>(url: string, config?: AxiosConfig) {
    return this.axiosInstance.delete<T>(url, config);
  }

  public patch<T, D = unknown>(url: string, data?: D, config?: AxiosConfig) {
    return this.axiosInstance.patch<T>(url, data, config);
  }

  public interceptors = {
    request: {
      use: (
        onFulfilled: (value: InternalAxiosRequestConfig) => InternalAxiosRequestConfig | Promise<InternalAxiosRequestConfig>,
        onRejected?: (error: AxiosError) => AxiosError | Promise<AxiosError>,
      ) => {
        this.axiosInstance.interceptors.request.use(onFulfilled, onRejected);
      },
    },
    response: {
      use: (
        onFulfilled: (value: AxiosResponse) => AxiosResponse | Promise<AxiosResponse>,
        onRejected?: (error: AxiosError) => AxiosError | Promise<AxiosError>,
      ) => {
        this.axiosInstance.interceptors.response.use(onFulfilled, onRejected);
      },
    },
  };
}

export default Axios;
